import React from 'react';
import { ShieldCheck, Clock, History, KeyRound, AlertTriangle } from 'lucide-react';

const BRUTE_FORCE_STAGES = [
  { fails: 5, lock: '5 Minuten', color: 'bg-yellow-100 text-yellow-900 border-yellow-300' },
  { fails: 10, lock: '15 Minuten', color: 'bg-orange-100 text-orange-900 border-orange-300' },
  { fails: 20, lock: '1 Stunde', color: 'bg-red-100 text-red-900 border-red-300' },
  { fails: 30, lock: '24 Stunden', color: 'bg-red-200 text-red-900 border-red-500 font-bold' },
];

export default function SectionSicherheitAudit() {
  return (
    <div className="space-y-4" data-testid="section-sicherheit-audit">
      <div>
        <h3 className="text-base font-bold text-slate-800 mb-1 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-green-600" />
          Sicherheit &amp; Audit
        </h3>
        <p className="text-xs text-slate-500">Vollständige Übersicht aller Sicherheitsmechanismen im QM8D-System.</p>
      </div>

      <div>
        <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-orange-600" /> Brute-Force-Schutz (progressiv)
        </h4>
        <div className="text-xs text-slate-600 mb-2">
          Der Schutz ist <strong>4-stufig</strong> und verlängert sich bei wiederholten Fehlversuchen.
          Pro IP-Adresse + Username werden fehlgeschlagene Login-Versuche gezählt.
        </div>
        <table className="w-full text-xs border-collapse">
          <thead><tr className="bg-slate-100"><th className="text-left p-2">Fehlversuche</th><th className="text-left p-2">Sperrzeit</th></tr></thead>
          <tbody>
            {BRUTE_FORCE_STAGES.map((s, i) => (
              <tr key={i} className={`border-t-2 ${s.color}`}>
                <td className="p-2 font-mono font-semibold">≥ {s.fails}</td>
                <td className="p-2">{s.lock}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-2 text-[11px] text-slate-500 font-mono">
          Quelle: backend/domains/auth/brute_force.py — AUTH_LOCKOUT_THRESHOLDS
        </div>
      </div>

      <div>
        <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5">
          <KeyRound className="w-3.5 h-3.5 text-purple-600" /> Zwei-Faktor-Authentifizierung (TOTP)
        </h4>
        <ul className="text-xs space-y-1 ml-4 list-disc text-slate-700">
          <li><strong>TOTP-Apps:</strong> Google Authenticator, Microsoft Authenticator, Authy, 1Password</li>
          <li><strong>10 Backup-Codes:</strong> bei Aktivierung werden 10 einmalig nutzbare Codes generiert (hashed in DB gespeichert)</li>
          <li><strong>Pflicht</strong> für Admin, GBL und ZQM — Login ohne TOTP nicht möglich</li>
          <li><strong>Wiederherstellung:</strong> Bei Handy-Verlust → Backup-Codes nutzen oder Admin-Reset anfragen</li>
        </ul>
      </div>

      <div>
        <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-blue-600" /> Session &amp; Token-Management
        </h4>
        <div className="grid sm:grid-cols-2 gap-2">
          <div className="p-2.5 rounded border border-slate-200 bg-white text-xs">
            <div className="font-semibold text-slate-800 mb-1">Access-Token</div>
            <ul className="ml-3 list-disc space-y-0.5 text-slate-600">
              <li>Lebensdauer: 15 Minuten</li>
              <li>Transport: HttpOnly-Cookie (Custom-Domain) oder Authorization-Header</li>
              <li>SameSite-Schutz gegen CSRF</li>
            </ul>
          </div>
          <div className="p-2.5 rounded border border-slate-200 bg-white text-xs">
            <div className="font-semibold text-slate-800 mb-1">Refresh-Token</div>
            <ul className="ml-3 list-disc space-y-0.5 text-slate-600">
              <li>Lebensdauer: 7 Tage</li>
              <li>Automatische Rotation bei Nutzung</li>
              <li>Bei Logout: Token-Blacklisting (sofort ungültig)</li>
            </ul>
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5">
          <History className="w-3.5 h-3.5 text-indigo-600" /> Login-Historie &amp; Audit-Log
        </h4>
        <ul className="text-xs space-y-1 ml-4 list-disc text-slate-700">
          <li><strong>Login-Historie:</strong> letzte <strong>50 Logins</strong> pro User werden gespeichert (IP, User-Agent, Zeitstempel)</li>
          <li><strong>Einsichtbar:</strong> <em>Mein Profil → Login-Historie</em></li>
          <li><strong>Audit-Log:</strong> alle sicherheitsrelevanten Aktionen werden protokolliert (Login, Logout, Password-Reset, Rollenänderung)</li>
          <li><strong>Admin-Zugriff:</strong> Vollständiges Audit-Log unter <em>System → Audit-Log</em></li>
          <li><strong>Verdächtige Aktivitäten:</strong> IP/UA-Wechsel werden automatisch im Audit-Log markiert</li>
        </ul>
      </div>

      <div className="p-3 rounded-lg bg-blue-50 border-l-4 border-blue-400 text-xs text-blue-900">
        <strong className="block mb-1">Passwort-Reset:</strong>
        JWT-Token per E-Mail, 30 Minuten gültig. Nach erfolgreichem Reset werden alle
        bestehenden Sessions dieses Nutzers zwangsweise ungültig (Blacklisting).
      </div>
    </div>
  );
}
